import { useState, useEffect, useRef } from "react";
import { InfoCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Layout,
  Card,
  Button,
  Form,
  Input,
  Table,
  Tabs,
  Modal,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  message,
} from "antd";
import { useSelector } from "react-redux";
import {
  getExamMethodInformation,
  submitAdultsExamResult,
} from "../../../../../../components/https/age-assessment-apis";
import {
  errorRequestHandel,
  ageAssessmentBasePath,
} from "../../../../../../helper";
import Method from "../../child/method/Method";
import { dentalIcons } from "../../../../../../dentail-indetifcation/utils-icons/dentalIcons";
import CustomPrimaryButton from "../../../../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import CustomButtonWithIcon from "../../../../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import { assessmentIcons } from "../../../../../utils/assessmentIcons";
import CustomCardWithHeader from "../../../../../../components/custom-card-with-header/CustomCardWithHeader";
import { validateNumericValue } from "../../../../../../utils/validators/methods/shared";
import { useNavigate } from "react-router-dom";
import ToothOrRadiographTab from "../../tooth-or-radiograph/ToothOrRadiographTab";
import PhysicalRadioGraph from "../../child/physical-radio-graph/PhysicalRadioGraph";
import { useSubscriptionGuard } from "../../../../../../hooks/subscription-hook/UseSubscriptionGuard";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

export default function IntactToothAnalysis() {
  useSubscriptionGuard();
  const navigate = useNavigate();
  const radioGraphContainerRef = useRef(null);
  const imageContainerRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRadioGraphFullscreen, setIsRadioGraphFullscreen] = useState(false);

  const AVAILABLE_METHODS = useSelector(
    (state) => state.ageAssessmentCase?.exameMethods || []
  );
  const currentCaseDetail = useSelector(
    (state) => state.ageAssessmentCase?.currentCaseDetail || null
  );
  let additional_images = currentCaseDetail?.additional_images || [];
  if (currentCaseDetail?.opg && currentCaseDetail.opg.trim() !== "") {
    additional_images = [currentCaseDetail.opg, ...additional_images]; // Insert at index 0
  }
  const { metaData } = useSelector((state) => state.general);

  // State for method selection
  const [selectedMethods, setSelectedMethods] = useState([20]); // Default to Cameriere method
  const [selectedMethod, setSelectedMethod] = useState(AVAILABLE_METHODS[0]);
  const [activeTableMethod, setActiveTableMethod] = useState(
    AVAILABLE_METHODS[0]
  ); // Separate state for table view

  // State for teeth data
  const [methodsData, setMethodsData] = useState({});
  const [examMethodData, setExamMethodData] = useState({});
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for tooth selection and form data
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [currentToothIndex, setCurrenToothIndex] = useState(0);
  const [currentStageIndex, setCurrenStageIndex] = useState(0);
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    lamendin: {},
    bang: {},
    collation: {},
  });

  const [matchedStages, setMatchedStages] = useState([]);
  // State for modal
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoType, setInfoType] = useState("");

  const metaDataTeeth = metaData.teeth.map(({ surfaces, id, iso, ...rest }) => {
    // Find a matching tooth in methodsData by iso
    const matchingTooth = methodsData[selectedMethod.slug]?.teeth.find(
      (tooth) => tooth.iso === iso
    );
    return {
      ...rest,
      iso,
      id: matchingTooth ? matchingTooth.id : id, // Replace id if match is found
    };
  });

  const firstQuadrant = metaDataTeeth.slice(0, 8); // Teeth 18-11
  const secondQuadrant = metaDataTeeth.slice(8, 16); // Teeth 21-28
  const thirdQuadrant = metaDataTeeth.slice(24, 32).reverse(); // Teeth 48-41
  const fourthQuadrant = metaDataTeeth.slice(16, 24).reverse(); // Teeth 31-38

  const getTeethList = (data) => {
    return data.flatMap((tooth) =>
      tooth.children.length > 0
        ? tooth.children.map((child) => ({
            iso: child.fdi,
            name: child.name,
            hasChildren: true,
            otherInfo: tooth,
          }))
        : null
    );
  };

  // Fetch method data
  const fetchMethodData = async (methodSlug) => {
    setLoading(true);
    try {
      const response = await getExamMethodInformation({
        slug: methodSlug === "collation" ? "lamendin" : methodSlug,
      });
      if (response.status === 200) {
        let dataObj = response?.data?.data;
        dataObj["teeth"] = getTeethList(dataObj.teeth);
        setMethodsData((prev) => ({
          ...prev,
          [methodSlug]: response?.data?.data,
        }));
        setExamMethodData(response?.data?.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      const defaultMethod = AVAILABLE_METHODS[0];
      if (defaultMethod) {
        await handleMethodToggle(defaultMethod.id);
      }
    };

    fetchInitialData();
  }, []);

  // Check if a method has any saved teeth
  const hasMethodData = (methodSlug) => {
    return Object.keys(formData[methodSlug] || {}).length > 0;
  };

  // Handle method selection
  const handleMethodSelect = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    // If method is not already selected, add it to selected methods
    if (!selectedMethods.includes(methodId)) {
      setSelectedMethods((prev) => [...prev, methodId]);
    }

    // Set as active method
    setSelectedMethod(method);

    // Also update the active table method to match
    setActiveTableMethod(method);

    // Check cache first
    if (!methodsData[method.slug]) {
      await fetchMethodData(method.slug);
    } else {
      // setStages(methodsData[method.slug][0].stages); // Use cached data
      setExamMethodData(methodsData[method.slug]);
    }

    // Clear selected tooth when changing methods
    setSelectedTooth(null);
  };
  // Handle method deletion (remove all data for a method)
  const handleDeleteMethod = (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    // Clear all data for this method
    setFormData((prev) => ({
      ...prev,
      [method.slug]: {},
    }));

    // If this was the selected method, switch to another method
    if (selectedMethod.id === methodId && selectedMethods.length > 1) {
      const nextMethodId = selectedMethods.find((id) => id !== methodId);
      const nextMethod = AVAILABLE_METHODS.find((m) => m.id === nextMethodId);
      setSelectedMethod(nextMethod);

      // Check cache first
      if (!methodsData[nextMethod.slug]) {
        fetchMethodData(nextMethod.slug);
      } else {
        setStages(methodsData[nextMethod.slug][0].stages); // Use cached data
        setExamMethodData(methodsData[nextMethod.slug]);
      }
    }

    // If this was the active table method, switch to another method
    if (activeTableMethod.id === methodId) {
      const nextMethodId = selectedMethods.find((id) => id !== methodId);
      const nextMethod = AVAILABLE_METHODS.find((m) => m.id === nextMethodId);
      setActiveTableMethod(nextMethod);
    }

    // Remove from selected methods
    setSelectedMethods((prev) => prev.filter((id) => id !== methodId));
  };

  // Handle changing the active method (for tooth selection and form)
  const handleChangeActiveMethod = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method || method.id === selectedMethod.id) return;

    setSelectedMethod(method);

    // Check cache first
    if (!methodsData[method.slug]) {
      await fetchMethodData(method.slug);
    } else {
      setStages(methodsData[method.slug][0].stages); // Use cached data
      setExamMethodData(methodsData[method.slug]);
    }

    // Clear selected tooth when changing methods
    setSelectedTooth(null);
  };

  // Handle changing the active table method (for preview table only)
  const handleChangeTableMethod = (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    // Only update the active table method, not the selected method
    setActiveTableMethod(method);
    handleMethodToggle(methodId);
  };

  // Handle tooth selection
  const handleToothClick = (tooth) => {
    setSelectedTooth(tooth);
    const currentData = formData[selectedMethod.slug]?.[tooth.iso] || {};
    if (currentData && Object.keys(currentData).length > 0) {
      form.setFieldsValue(formData[selectedMethod.slug][tooth.iso] || {});
    } else {
      form.resetFields();
    }
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    if (!selectedTooth) return;

    setFormData((prev) => ({
      ...prev,
      [selectedMethod.slug]: {
        ...prev[selectedMethod.slug],
        [selectedTooth.id]: {
          ...prev[selectedMethod.slug][selectedTooth.id],
          tooth: selectedTooth,
          [field]: value,
        },
      },
    }));
  };

  const handleSave = () => {
    if (!selectedTooth) return;

    form
      .validateFields()
      .then((values) => {
        // All validation passed, update your formData state here
        setFormData((prev) => ({
          ...prev,
          [selectedMethod.slug]: {
            ...prev[selectedMethod.slug],
            [selectedTooth.iso]: {
              ...prev[selectedMethod.slug][selectedTooth.iso],
              tooth: selectedTooth,
              ...values, // Use the validated values from the form
            },
          },
        }));

        // Clear selection after save
        setSelectedTooth(null);
        form.resetFields();
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const getToothStatus = (toothIso) => {
    // Check if the method exists in the data
    if (!formData.hasOwnProperty(selectedMethod.slug)) {
      return false; // Method slug not found
    }

    // Get the method's tooth data
    const methodTeeth = formData[selectedMethod.slug];

    // Iterate over teeth in the method
    for (const key in methodTeeth) {
      if (methodTeeth[key].tooth && methodTeeth[key].tooth.iso === toothIso) {
        return true; // Tooth with given ISO found
      }
    }

    return false; // Tooth not found
  };

  // Delete tooth data
  const handleDelete = (methodSlug, toothId) => {
    setFormData((prev) => {
      const methodData = { ...prev[methodSlug] };
      delete methodData[toothId];

      return {
        ...prev,
        [methodSlug]: methodData,
      };
    });
  };

  // Show info modal
  const showInfoModal = (type) => {
    setInfoType(type);
    setInfoModalVisible(true);
  };

  // Prepare table data for preview
  const getTableData = (methodSlug) => {
    const methodData = formData[methodSlug];
    if (!methodData) return [];
    return Object.entries(methodData).map(([id, data]) => ({
      key: id,
      iso: data.tooth.iso,
      name: data.tooth.name,
      ...data,
    }));
  };

  // Check if any method has data
  const hasAnyData = Object.keys(formData).some(
    (method) => Object.keys(formData[method]).length > 0
  );

  // Get methods that will be submitted (have at least one tooth saved)
  const getMethodsToSubmit = () => {
    return AVAILABLE_METHODS.filter((method) => hasMethodData(method.slug));
  };

  // Get column definitions based on method
  const getColumns = (methodSlug) => {
    const baseColumns = [
      {
        title: "Tooth Position",
        dataIndex: "",
        key: "iso",
        render: (item) => "ISO " + item.iso,
      },
      {
        title: "Tooth Name",
        dataIndex: "name",
        key: "name",
      },
    ];

    const actionColumn = {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(methodSlug, record.key)}
        />
      ),
    };

    if (methodSlug === "lamendin" || methodSlug === "collation") {
      return [
        ...baseColumns,
        {
          title: "Root height",
          dataIndex: "root_height",
          key: "root_height",
        },
        {
          title: "Periodental Recession",
          dataIndex: "periodontal_recession",
          key: "periodontal_recession",
        },
        {
          title: "Root Translucency",
          dataIndex: "root_translucency",
          key: "rootLengthMesial",
        },
        actionColumn,
      ];
    } else if (methodSlug === "bang") {
      return [
        ...baseColumns,
        {
          title: "Root Translucency",
          dataIndex: "root_translucent",
          key: "rootLengthMesial",
        },

        actionColumn,
      ];
    }

    return [...baseColumns, actionColumn];
  };

  // Render form fields based on method
  const renderFormFields = () => {
    if (!selectedTooth || !selectedMethod) return null;

    const currentData = formData[selectedMethod.slug][selectedTooth.id] || {};

    if (
      selectedMethod.slug === "lamendin" ||
      selectedMethod.slug === "collation"
    ) {
      return (
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <Form.Item
                name="root_height"
                rules={[validateNumericValue("Root Height")]}
                label={
                  <Space>
                    Root Height
                    <Button
                      type="text"
                      icon={<InfoCircleOutlined />}
                      onClick={() => showInfoModal("root_height")}
                      style={{ padding: 0, margin: 0 }}
                    />
                  </Space>
                }
              >
                <Input placeholder="Enter value" />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="periodontal_recession"
                rules={[validateNumericValue("Periodental Recession")]}
                label={
                  <Space>
                    Periodental Recession
                    <Button
                      type="text"
                      icon={<InfoCircleOutlined />}
                      onClick={() => showInfoModal("periodontal_recession")}
                      style={{ padding: 0 }}
                    />
                  </Space>
                }
              >
                <Input placeholder="Enter value" />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="root_translucency"
                rules={[validateNumericValue("Root Translucency")]}
                label={
                  <Space>
                    Root Translucency
                    <Button
                      type="text"
                      icon={<InfoCircleOutlined />}
                      onClick={() => showInfoModal("root_translucency")}
                      style={{ padding: 0 }}
                    />
                  </Space>
                }
              >
                <Input placeholder="Enter value" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      );
    } else if (selectedMethod.slug === "bang") {
      return (
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <Form.Item
                name="root_translucent"
                rules={[validateNumericValue("Root Translucency")]}
                label={
                  <Space>
                    Root Translucency
                    <Button
                      type="text"
                      icon={<InfoCircleOutlined />}
                      onClick={() => showInfoModal("root_translucent")}
                      style={{ padding: 0 }}
                    />
                  </Space>
                }
              >
                <Input placeholder="Enter value" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      );
    }

    return null;
  };

  const removeQuadrantPrefix = (name) => {
    return name.replace(/^(Upper|Lower) (Left|Right) /, "");
  };

  // Custom tab items for the preview table

  const getTabItems = () => {
    return selectedMethods
      ?.filter((methodId) => {
        const method = AVAILABLE_METHODS?.find((m) => m.id === methodId);
        return method && Object.keys(formData[method.slug] || {}).length > 0; // ✅ Only keep methods with data
      })
      .map((methodId) => {
        const method = AVAILABLE_METHODS?.find((m) => m.id === methodId);
        return {
          key: methodId.toString(),
          label: `${method.title}`,
          children: (
            <CustomCardWithHeader title={"Preview Selections"}>
              <Table
                dataSource={getTableData(method.slug)}
                columns={getColumns(method.slug)}
                pagination={false}
                locale={{
                  emptyText: (
                    <div style={{ padding: "20px 0", textAlign: "center" }}>
                      No teeth data recorded yet
                    </div>
                  ),
                }}
              />
            </CustomCardWithHeader>
          ),
        };
      });
  };

  const getMethodStatus = (method) => {
    // Check if the method exists in the data
    if (!formData.hasOwnProperty(method.slug)) {
      return "none"; // Method slug not found
    }
    // Get the method's tooth data
    const methodTeeth = formData[method.slug];
    // If no teeth exist, return "selected"
    if (Object.keys(methodTeeth).length > 0) {
      return "completed";
    }
    return "none";
  };
  const methodSlugMap = {
    20: "lamendin",
    21: "bang",
    22: "collation",
  };
  const hasAnyNonCollationCompleted = () => {
    return [20, 21].some((id) => {
      const methodSlug = methodSlugMap[id];
      const method = AVAILABLE_METHODS.find((m) => m.slug === methodSlug);
      return method && getMethodStatus(method) === "completed";
    });
  };

  const isCollationCompleted = () => {
    const method = AVAILABLE_METHODS.find((m) => m.id === 22);
    return method && getMethodStatus(method) === "completed";
  };
  const handleMethodToggle = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;
    methodId = method.id;
    let shouldChangeSelectMethod = true;

    setSelectedMethods((prevSelected) => {
      // --- Handle special case: selecting Collation (22) ---
      if (methodId === 22) {
        if (hasAnyNonCollationCompleted() && !prevSelected.includes(22)) {
          const userConfirmed = window.confirm(
            "Selecting Collation will remove your current progress. Do you want to continue?"
          );
          if (!userConfirmed) {
            shouldChangeSelectMethod = false;
            return prevSelected;
          }

          setFormData({
            lamendin: {},
            bang: {},
            collation: {},
          });
          setSelectedTooth(null);
          return [22];
        }

        return [methodId]; // Already selected, do nothing
      }

      // --- Handle switching away from Collation (22) ---
      if (prevSelected.includes(22)) {
        if (isCollationCompleted()) {
          const userConfirmed = window.confirm(
            "Switching from Collation will remove your current progress. Do you want to continue?"
          );
          if (!userConfirmed) {
            shouldChangeSelectMethod = false;
            return prevSelected;
          }

          setFormData((prev) => ({
            lamendin: prev.lamendin || {},
            bang: prev.bang || {},
            collation: {},
          }));
          setSelectedTooth(null);

          return [methodId];
        }
      }

      if (!prevSelected.includes(methodId) && shouldChangeSelectMethod) {
        return [...prevSelected, methodId];
      }
      setSelectedTooth(null);

      return prevSelected; // already selected, don’t remove
    });
    if (shouldChangeSelectMethod) {
      setSelectedMethod(method);
      if (!methodsData[method.slug]) {
        await fetchMethodData(method.slug);
      } else {
        setExamMethodData(methodsData[method.slug]);
      }
      setActiveTableMethod(method);
      setSelectedTooth(null);
    }
  };

  const getIsToothEnabled = (item) => {
    return examMethodData.teeth.some((tooth) => tooth.iso == item.iso);
  };

  const preparePayload = (formData, availableMethods, projectId) => {
    let methodsPayload = [];

    Object.keys(formData).forEach((methodSlug) => {
      // Find method by slug to get the ID
      const method = availableMethods.find((m) => m.slug === methodSlug);
      if (!method) return; // Skip if method is not found

      // Extract teeth data for this method
      const teethArray = Object.keys(formData[methodSlug]).map((toothKey) => {
        const toothData = formData[methodSlug][toothKey];
        let toothPayload = {};

        const toothDetails = methodsData[methodSlug].teeth.find(
          (tooth) => tooth.iso === toothData.tooth.iso
        );

        if (toothDetails !== null) {
          toothPayload = {
            id: toothDetails?.otherInfo?.id,
            child: {
              name: toothData.tooth.name,
              fdi: toothData.tooth.iso,
            },
          };
        }

        // Merge all tooth-related data except the nested tooth object
        Object.keys(toothData).forEach((key) => {
          if (key !== "tooth") {
            toothPayload[key] = toothData[key];
          }
        });

        return toothPayload;
      });

      // Base payload structure for method
      const methodPayload = {
        id: method.id === 22 ? 20 : method?.id,
        teeth: teethArray,
      };

      // Add is_collation for methods collation
      if (method.id === 22) {
        methodPayload.is_collation = 1;
      } else {
        methodPayload.is_collation = 0;
      }

      // Push method with its teeth to the payload
      methodsPayload.push(methodPayload);
    });
    methodsPayload = methodsPayload.filter((mth) => mth.teeth.length);
    // Final payload structure
    return {
      project_id: projectId,
      methods: methodsPayload,
    };
  };

  const handleProceed = async () => {
    try {
      const payload = preparePayload(
        formData,
        AVAILABLE_METHODS,
        currentCaseDetail?.project_id
      );
      setLoading(true);
      const response = await submitAdultsExamResult({ payload: payload });
      if (response.status === 200) {
        setLoading(false);
        message.success("Exam result has been submitted successfully");
        navigate(`${ageAssessmentBasePath}/cases/details`, {
          state: { projectId: currentCaseDetail?.project_id },
        });
      }
    } catch (error) {
      setLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  const getguidLinesImage = () => {
    const currentGuidelines = methodsData[selectedMethod.slug]?.guidlines || [];

    const foundGuideline = currentGuidelines.find(
      (guideline) => guideline.shortIdentifier === infoType
    );

    return foundGuideline ? foundGuideline : null;
  };

  const guideLineObject = getguidLinesImage(infoType);

  const onCancel = () => {
    if (
      Object.keys(formData).some(
        (methodSlug) => Object.keys(formData[methodSlug]).length > 0
      )
    ) {
      const confirmCancel = window.confirm("Are you sure you want to cancel?");
      if (!confirmCancel) {
        return; // Stop if the user cancels
      }
    }
    window.history.back(); // Go back to the previous page
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (imageContainerRef.current?.requestFullscreen) {
        imageContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };
  const toggleRadioGrapghFullscreen = () => {
    if (!isRadioGraphFullscreen) {
      if (radioGraphContainerRef.current?.requestFullscreen) {
        radioGraphContainerRef.current.requestFullscreen();
        setIsRadioGraphFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsRadioGraphFullscreen(false);
      }
    }
  };
  return (
    <div style={{ minHeight: "100vh", background: "#f9fcff" }}>
      {/* Breadcrumb */}

      {/* Method Selection */}
      <Card style={{ marginBottom: 24 }}>
        <h4>Method Selection</h4>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Select one or more methods for age assessment.
        </Paragraph>

        <Row gutter={[16, 16]}>
          {AVAILABLE_METHODS.map((method, index) => {
            return (
              <Col md={6} key={index}>
                <Method
                  item={method}
                  selectedMethod={selectedMethod}
                  status={getMethodStatus(method)}
                  onClick={() => handleMethodToggle(method.id)}
                />
              </Col>
            );
          })}
        </Row>
      </Card>

      <div className="tabs_section">
        <ToothOrRadiographTab
          selectdTab={selectedTab}
          onRadiographClick={() => setSelectedTab(1)}
          onToothClick={() => setSelectedTab(0)}
        />
      </div>

      {/* Tooth Selection */}
      {selectedTab === 0 ? (
        <>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "60px 0",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <Card style={{ marginBottom: 24 }}>
              <Title level={4}>Tooth Analysis</Title>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Select a tooth from the diagram and enter it's corresponding
                values in the designated section.
              </Paragraph>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <Row
                  gutter={[10, 10]}
                  className="dental_record_quadrant_section"
                >
                  {[
                    firstQuadrant,
                    secondQuadrant,
                    thirdQuadrant,
                    fourthQuadrant,
                  ].map((quadrant, quadrantIndex) => (
                    <Col md={12} key={quadrantIndex}>
                      <div className="dental_record_quadrant">
                        <Row>
                          {quadrant.map((item, index) => {
                            let isToothEnabled = getIsToothEnabled(item);
                            let isToothActive = selectedTooth?.iso === item.iso;
                            return (
                              <Col md={3} key={index}>
                                <div
                                  className="dental_record_singel_tooth"
                                  style={{
                                    background: !isToothEnabled
                                      ? "#D3D3D3"
                                      : isToothActive
                                      ? "#20c3ff"
                                      : "",
                                    color: isToothActive ? "white" : "",
                                    border: "1px solid grey",
                                    borderRadius: 10,
                                    margin: "0 3px",
                                    height: 80,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                    textAlign: "center",
                                    cursor: !isToothEnabled
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: !isToothEnabled ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isToothEnabled) {
                                      return;
                                    }
                                    handleToothClick(item);
                                  }}
                                >
                                  {getToothStatus(item.iso) && (
                                    <img
                                      src={dentalIcons.dentalRecordAdded}
                                      className="dental_record_completed_icon"
                                    />
                                  )}
                                  <p
                                    className="record_number"
                                    style={{
                                      height: 30,
                                      width: 25,
                                      fontWeight: "600",
                                      color: isToothActive ? "white" : "",
                                    }}
                                  >
                                    {item.iso}
                                  </p>
                                  <small>
                                    {removeQuadrantPrefix(item.name)}
                                  </small>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Card>
          )}
        </>
      ) : (
        <div>
          {additional_images.length > 0 ? (
            <PhysicalRadioGraph
              onFullScreen={toggleRadioGrapghFullscreen}
              radioGraphContainerRef={radioGraphContainerRef}
            />
          ) : (
            <Card style={{ marginBottom: 24 }} className="_antCard">
              <div className="readio_graph_not_uploaded">
                No radiographic images uploaded
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Tooth Data Form */}
      {selectedTooth && (
        <CustomCardWithHeader
          title={`${selectedTooth.name} -  ISO ${selectedTooth.iso}`}
        >
          <Card style={{ marginBottom: 24 }}>
            <div style={{ padding: "0 24px 24px" }}>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                Record dental characteristics for the selected tooth
              </Paragraph>

              {renderFormFields()}
              <CustomButtonWithIcon title="Save" onClick={handleSave} />
            </div>
          </Card>
        </CustomCardWithHeader>
      )}

      {/* Preview Selections */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          Preview Selections
        </Title>

        <Tabs
          activeKey={activeTableMethod.id.toString()}
          onChange={(key) => handleChangeTableMethod(Number.parseInt(key))}
          items={getTabItems()}
        />
      </Card>

      {/* Action Buttons */}

      <Row
        gutter={[16, 16]}
        justify={"space-between"}
        style={{ padding: "0 10px" }}
      >
        <CustomButtonWithIcon
          title="Cancel"
          icon={assessmentIcons.cancel}
          color={"#DC1919"}
          onClick={onCancel}
        />
        <CustomPrimaryButton
          title="Proceed"
          icon={assessmentIcons.proceed}
          disabled={!hasAnyData}
          onClick={handleProceed}
        />
      </Row>

      {/* Info Modal */}
      <Modal
        title={guideLineObject?.identifier}
        open={infoModalVisible}
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setInfoModalVisible(false)}
          >
            OK
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center" }}>
          <img
            src={guideLineObject?.image}
            alt={`${infoType} example`}
            style={{
              maxWidth: "100%",
              borderRadius: "8px",
              marginBottom: 16,
              height: 400,
            }}
          />
          <Title level={5} style={{ marginBottom: 8 }}>
            {guideLineObject?.identifier}
          </Title>
          <Paragraph type="secondary">{guideLineObject?.description}</Paragraph>
        </div>
      </Modal>
    </div>
  );
}
